import { type CreateCloudinaryUploadSignatureInput } from '#app/__generated__/api.js';
import { type useGetCloudinarySignatureMutation } from '#app/__generated__/relay/useGetCloudinarySignatureMutation.graphql.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import {
  type CommitContraMutationFunction,
  useContraMutation,
} from '#app/hooks/useContraMutation.js';
import { type CloudinarySignatureOptions } from '#app/hooks/useUpload.js';
import { buildCloudinaryTransforms } from '#app/utilities/cloudinaryTransforms/buildCloudinaryTransforms.js';
import { getCloudinaryTransformsForCropping } from '#app/utilities/getCloudinaryTransformsForCropping.js';
import { graphql } from '@contra/relay';
import { captureException } from '@contra/sentry/react';
import { useCallback, useState } from 'react';

type ImageDimensions = {
  croppedHeight: number;
  croppedWidth: number;
  originalHeight: number;
  originalWidth: number;
  x: number;
  y: number;
};

const validateImageNeedsTransform = ({
  croppedHeight,
  croppedWidth,
  originalHeight,
  originalWidth,
  x,
  y,
}: ImageDimensions): boolean => {
  return !(
    x >= 0 &&
    y >= 0 &&
    croppedHeight <= originalHeight &&
    croppedWidth <= originalWidth &&
    croppedHeight + y <= originalHeight &&
    croppedWidth + x <= originalWidth
  );
};

const createCloudinaryParameters = ({
  croppedAreaData,
  mediaSize,
}: Parameters<typeof getCloudinaryTransformsForCropping>[0]): string => {
  const transformArray = getCloudinaryTransformsForCropping({
    croppedAreaData,
    mediaSize,
  });
  const transformations = buildCloudinaryTransforms(...transformArray);

  return transformations;
};

type GetSignedTransformation = (options: {
  input: CreateCloudinaryUploadSignatureInput;
  uploadCallback?: (
    cloudinarySignatureOptions: CloudinarySignatureOptions,
  ) => void;
}) => void;

type UseGetCloudinarySignature = {
  cloudinarySignatureOptions: CloudinarySignatureOptions | null;
  createCloudinaryParameters: typeof createCloudinaryParameters;
  getCloudinarySignature: CommitContraMutationFunction<useGetCloudinarySignatureMutation>;
  getSignedTransformation: GetSignedTransformation;
  isLoadingSignature: boolean;
  validateImageNeedsTransform: typeof validateImageNeedsTransform;
};

export const useGetCloudinarySignature = (): UseGetCloudinarySignature => {
  const [cloudinarySignatureOptions, setCloudinarySignatureOptions] =
    useState<CloudinarySignatureOptions | null>(null);
  const toast = useToast();
  const [getCloudinarySignature, isLoadingSignature] =
    useContraMutation<useGetCloudinarySignatureMutation>(graphql`
      mutation useGetCloudinarySignatureMutation(
        $input: CreateCloudinaryUploadSignatureInput!
      ) {
        createCloudinaryUploadSignature(input: $input) {
          errors {
            __typename
            message
          }
          signature
          timestamp
          apiKey
        }
      }
    `);

  const getSignedTransformation = useCallback<GetSignedTransformation>(
    ({
      input: { customCoordinates, transformation, uploadPreset },
      uploadCallback,
    }) => {
      getCloudinarySignature({
        onCompleted: (errors, response) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;

          if (hasErrors) {
            if (payloadErrors) {
              toast.error('An unexpected error occurred. Please try again.');
              captureException(payloadErrors[0]);
            } else if (apiErrors) {
              toast.error(apiErrors[0].message);
            }

            return;
          }

          const { createCloudinaryUploadSignature } = response;
          if (
            createCloudinaryUploadSignature?.signature &&
            createCloudinaryUploadSignature?.timestamp &&
            createCloudinaryUploadSignature?.apiKey &&
            transformation
          ) {
            const signatureOptions = {
              apiKey: createCloudinaryUploadSignature.apiKey,
              signature: createCloudinaryUploadSignature.signature,
              timestamp: createCloudinaryUploadSignature.timestamp,
              transformation,
            };
            setCloudinarySignatureOptions(signatureOptions);
            uploadCallback?.(signatureOptions);
          }
        },
        onError: () => {
          toast.error('An unexpected error occurred. Please try again.');
        },
        variables: {
          input: {
            customCoordinates,
            transformation,
            uploadPreset,
          },
        },
      });
    },
    [getCloudinarySignature, toast],
  );

  return {
    cloudinarySignatureOptions,
    createCloudinaryParameters,
    getCloudinarySignature,
    getSignedTransformation,
    isLoadingSignature,
    validateImageNeedsTransform,
  };
};
